import { splitInlineGuidance } from '@/entities/templates';

import { describeNoteLine, type NoteLineDef } from './noteView';

// Display helpers of the block template editor. They read the template and
// never decide what a note shows: line visibility is scribe's.

// The view filter: show everything, or hide the linked lines the values do
// not reveal.
export type NoteEditorViewFilter = 'all' | 'hideLinked';

// The sections and lines links are read from: a template's, or the lines a
// note shows.
export type NoteLinkSections = {
  sections: { Title: string; Lines: NoteLineDef[] }[];
};

type BlockHome = {
  section: NoteLinkSections['sections'][number];
  line: NoteLineDef;
};

const indexBlockHomes = (template: NoteLinkSections) =>
  new Map(
    template.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.map(
          (block) => [block.ID, { section, line }] as [string, BlockHome],
        ),
      ),
    ),
  );

export type NoteLinkOrigin = {
  // 1: a condition of the line itself; 2: a condition of the line holding
  // a level-1 trigger; and so on up the chain.
  level: number;
  blockId: string;
  // Empty when the trigger block is not in the template.
  lineId: string;
  sectionTitle: string;
  lineLabel: string;
  values: string[];
};

// Where a linked line comes from ("Link origins"): its own
// conditions, then the conditions of the lines their triggers sit on. A
// line already walked is not walked again, so a cycle (which scribe
// refuses) still ends.
export const listNoteLinkOrigins = (
  template: NoteLinkSections,
  lineId: string,
): NoteLinkOrigin[] => {
  const homes = indexBlockHomes(template);
  const lines = new Map(
    template.sections.flatMap((section) =>
      section.Lines.map((line) => [line.ID, line] as const),
    ),
  );
  const origins: NoteLinkOrigin[] = [];
  const walked = new Set([lineId]);
  let level = 1;
  let current = [lineId];
  while (current.length) {
    const next: string[] = [];
    for (const id of current) {
      for (const condition of lines.get(id)?.ShowWhen ?? []) {
        const home = homes.get(condition.BlockID);
        origins.push({
          level,
          blockId: condition.BlockID,
          lineId: home?.line.ID ?? '',
          sectionTitle: home?.section.Title ?? '',
          lineLabel: describeNoteLine(home?.line),
          values: condition.Values,
        });
        if (home && !walked.has(home.line.ID)) {
          walked.add(home.line.ID);
          next.push(home.line.ID);
        }
      }
    }
    current = next;
    level += 1;
  }
  return origins;
};

// "Section → values", the way a link origin is named; a trigger that is not
// in the template is named by the caller's text.
export const formatNoteLinkOrigin = (
  origin: NoteLinkOrigin,
  unknown: string,
) =>
  origin.lineId
    ? `${origin.sectionTitle} → ${origin.values.join(', ')}`
    : unknown;

// "Linked from": the conditions of the line itself.
export const formatNoteLinkedFrom = (
  origins: NoteLinkOrigin[],
  unknown: string,
) =>
  origins
    .filter((origin) => origin.level === 1)
    .map((origin) => formatNoteLinkOrigin(origin, unknown))
    .join('; ');

export type NoteLinkDependent = {
  lineId: string;
  sectionTitle: string;
  lineLabel: string;
  values: string[];
};

// The lines a trigger block shows, with the values that show them.
export const listNoteLinkDependents = (
  template: NoteLinkSections,
  blockId: string,
): NoteLinkDependent[] =>
  template.sections.flatMap((section) =>
    section.Lines.flatMap((line) =>
      line.ShowWhen.filter((condition) => condition.BlockID === blockId).map(
        (condition) => ({
          lineId: line.ID,
          sectionTitle: section.Title,
          lineLabel: describeNoteLine(line),
          values: condition.Values,
        }),
      ),
    ),
  );

// One option a line is opened by: a value of a condition of the line itself,
// on a block the template holds.
export type NoteLinkOpener = {
  blockId: string;
  lineId: string;
  lineLabel: string;
  value: string;
};

// An opener with its option's label, as a link mark names it.
export type NamedNoteLinkOpener = NoteLinkOpener & { label: string };

export const listNoteLineOpeners = (
  template: NoteLinkSections,
  lineId: string,
): NoteLinkOpener[] =>
  listNoteLinkOrigins(template, lineId)
    .filter((origin) => origin.level === 1 && origin.lineId)
    .flatMap(({ blockId, lineId: openerLineId, lineLabel, values }) =>
      values.map((value) => ({
        blockId,
        lineId: openerLineId,
        lineLabel,
        value,
      })),
    );

// The options a linked line's mark names: in a note those chosen now, which
// opened the line; where none is chosen, as in a template, every option of
// its condition. The mark shows the first of them.
export const listShownLineOpeners = <T extends NoteLinkOpener>(
  openers: T[],
  isChosen: (opener: NoteLinkOpener) => boolean = () => false,
) => {
  const chosen = openers.filter(isChosen);
  return chosen.length ? chosen : openers;
};

// The lines one option of a block opens, in the order they are listed.
export const listNoteOptionLines = (
  template: NoteLinkSections,
  blockId: string,
  value: string,
) =>
  listNoteLinkDependents(template, blockId)
    .filter((dependent) => dependent.values.includes(value))
    .map((dependent) => dependent.lineId)
    .filter((lineId, index, all) => all.indexOf(lineId) === index);

export const listInlineGuidance = (text: string) =>
  splitInlineGuidance(text)
    .filter((segment) => segment.guidance)
    .map((segment) => segment.text);
